import { Box, styled } from "@mui/material";
import { ArrowLeft, ArrowRight, Clear } from "@procore/core-icons";
import { Button, Spinner, Typography } from "@procore/core-react";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { PROCORE_TOP_BAR, TOOLBAR_Z_INDEX } from "components/toolbars/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraType, renderActions } from "features/render";
import {
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
    selectSceneId,
} from "slices/explorer";
import { AsyncStatus } from "types/misc";

import { downloadDrawingData } from "./downloadDrawingData";
import { LevelsDrawerMobile } from "./levelsDrawerMobile";
import {
    navigateToDrawing,
    selectCurrentDrawing,
    selectTempDrawing,
    selectTempLevel,
    setLargeMapMobileOpen,
    setTempDrawingId,
} from "./minimapSlice";
import {
    drawingToModelSpace,
    drawLargeMapPositionIndicator,
    EYE_ELEVATION_OFFSET,
    modelToDrawingSpace,
    resizeCanvasForDpr,
} from "./miniMapUtils";
import { NAVIGATION_PIN_GRACE, SHEET_FLY_TIME } from "./navigationAnimations";
import {
    buildTilingRequest,
    type DrawingPaint,
    makeBitmapPaint,
    makeTiledPaint,
    useTiledLargeMap,
} from "./tiledDrawing";
import type { DrawingSpacePosition } from "./types";
import { useDrawingImage } from "./useDrawingImage";

/** Close animation length, shared with the canvas release below. */
const CLOSE_TRANSITION_MS = 300;

const LargeMapContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "open" && prop !== "isFullscreenMode",
})<{ open: boolean; isFullscreenMode: boolean }>(({ open, isFullscreenMode }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fafafa",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transform: open ? "translateY(0)" : "translateY(20px)",
    transition: `opacity ${CLOSE_TRANSITION_MS}ms ease-out, visibility ${CLOSE_TRANSITION_MS}ms ease-out, transform ${CLOSE_TRANSITION_MS}ms ease-out`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    pointerEvents: open ? "auto" : "none",
    // Higher z-index when in fullscreen mode to cover top bar
    zIndex: isFullscreenMode ? TOOLBAR_Z_INDEX.TOP_BAR + 10 : TOOLBAR_Z_INDEX.LARGE_MAP_MOBILE,
}));

const CanvasContainer = styled(Box)({
    flex: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
});

const LoadingOverlay = styled(Box)({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    gap: 16,
});

const NavArrowsContainer = styled(Box)({
    position: "absolute",
    bottom: 24,
    right: 24,
    display: "flex",
    gap: 8,
    zIndex: 2,
});

const HelperTextContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "centered" && prop !== "topOffset",
})<{ centered: boolean; topOffset: number }>(({ centered, topOffset }) => ({
    position: "absolute",
    top: topOffset,
    left: centered ? "50%" : "auto",
    right: centered ? "auto" : 24,
    transform: centered ? "translateX(-50%)" : "none",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    backgroundColor: "rgba(227, 230, 232, 1)",
    borderRadius: 8,
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 3,
    width: 347,
    maxWidth: "calc(100% - 48px)",
    boxSizing: "border-box",
}));

const CloseButtonContainer = styled(Box)({
    position: "absolute",
    top: 24,
    right: 24,
    zIndex: 4,
});

interface LargeMapMobileProps {
    open: boolean;
}

const LONG_PRESS_DURATION = 500;
const LONG_PRESS_MOVE_THRESHOLD = 10;
const MIN_ROTATION_DISTANCE = 5;
const HELPER_TEXT_CENTER_BREAKPOINT = 500;
const FULLSCREEN_HEIGHT_BREAKPOINT = 500;

// Layout breakpoint calculation
const LEVELS_DRAWER_WIDTH = 377;
const NAV_ARROWS_WIDTH = 34 * 2 + 8; // 2 buttons + gap
const LAYOUT_GAP = 50;
const NARROW_LAYOUT_BREAKPOINT = LEVELS_DRAWER_WIDTH + NAV_ARROWS_WIDTH + LAYOUT_GAP + 24 + 24; // drawer + arrows + gap + margins

export function LargeMapMobile({ open }: LargeMapMobileProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [canvasSize, setCanvasSize] = useState<[number, number]>([300, 300]);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const tempDrawing = useAppSelector(selectTempDrawing);
    const tempLevel = useAppSelector(selectTempLevel);
    const tilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useAppSelector(selectSceneId);
    // Only decode while the large map is open. `tempDrawing` falls back to the
    // current drawing even when closed, so decoding unconditionally would keep
    // a second ~100MB bitmap of the current drawing alive alongside the always-
    // mounted mini map's copy.
    const img = useDrawingImage(open ? tempDrawing : null);
    // Tiled route (BM-13597): paints viewport tiles instead of one
    // full-resolution bitmap; `img` stays undefined for tiled drawings.
    const tiledMap = useTiledLargeMap(open ? tempDrawing : null);
    const {
        state: { view },
    } = useExplorerGlobals();

    const [placedPosition, setPlacedPosition] = useState<DrawingSpacePosition | null>(null);
    // Store the image layout info for position calculations
    const imageLayoutRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

    const [scale, setScale] = useState(1);
    const [panOffset, setPanOffset] = useState<vec2>(vec2.fromValues(0, 0));
    const [levelsDrawerOpen, setLevelsDrawerOpen] = useState(true);
    const [isInsideLevel, setIsInsideLevel] = useState(false);
    const [showHelperText, setShowHelperText] = useState(true);

    const lastPinchDistRef = useRef<number | null>(null);
    const pinchScreenPosRef = useRef<vec2 | null>(null);

    const handleDismissHelperText = useCallback(() => {
        setShowHelperText(false);
    }, []);

    useEffect(() => {
        if (open) {
            dispatch(setTempDrawingId(currentDrawing?.id ?? null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dispatch]);

    useEffect(() => {
        if (!open) {
            setPlacedPosition(null);
            setScale(1);
            setPanOffset(vec2.fromValues(0, 0));
            setLevelsDrawerOpen(true);
            setIsInsideLevel(false);
            dispatch(setTempDrawingId(null));
        }
    }, [open, dispatch]);

    const drawingIds = useMemo(() => tempLevel?.drawingIds ?? [], [tempLevel?.drawingIds]);
    const totalDrawings = drawingIds.length;
    const currentDrawingIndex = tempDrawing ? drawingIds.indexOf(tempDrawing.id) : -1;
    const isNarrowLayout = canvasSize[0] < NARROW_LAYOUT_BREAKPOINT;
    const canNavigate = isInsideLevel && totalDrawings > 1 && !isNarrowLayout;
    // Fullscreen mode when canvas height is small - covers top bar
    const isFullscreenMode = canvasSize[1] <= FULLSCREEN_HEIGHT_BREAKPOINT;
    // Top offset for helper text and drawer - relative to canvas top when fullscreen, otherwise below top bar
    const topOffset = isFullscreenMode ? 24 : PROCORE_TOP_BAR.HEIGHT + 24;

    const handlePrevDrawing = useCallback(() => {
        if (!canNavigate) {
            return;
        }
        const prevIndex = currentDrawingIndex <= 0 ? totalDrawings - 1 : currentDrawingIndex - 1;
        dispatch(setTempDrawingId(drawingIds[prevIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
    }, [canNavigate, currentDrawingIndex, totalDrawings, drawingIds, dispatch]);

    const handleNextDrawing = useCallback(() => {
        if (!canNavigate) {
            return;
        }
        const nextIndex = currentDrawingIndex >= totalDrawings - 1 ? 0 : currentDrawingIndex + 1;
        dispatch(setTempDrawingId(drawingIds[nextIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
    }, [canNavigate, currentDrawingIndex, totalDrawings, drawingIds, dispatch]);

    const handleClose = useCallback(() => {
        dispatch(setLargeMapMobileOpen(false));
    }, [dispatch]);

    useEffect(() => {
        if (!open || !containerRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setCanvasSize([width, height]);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [open]);

    // The map is hidden with CSS rather than unmounted, and the render effect
    // below early-returns while closed, so without this the canvas keeps its
    // last backing store — ~11MB at a landscape phone's device resolution —
    // for the rest of the session once the map has been opened even once.
    // `useTiledLargeMap` already drops the tile cache and base preview on
    // close, so the canvas was the only piece still held. Released after the
    // fade-out rather than on the state change, so the map doesn't blank
    // mid-transition.
    useEffect(() => {
        if (open) {
            return;
        }

        const timer = setTimeout(() => {
            const canvas = canvasRef.current;
            if (canvas) {
                canvas.width = 0;
                canvas.height = 0;
            }
        }, CLOSE_TRANSITION_MS);

        return () => clearTimeout(timer);
    }, [open]);

    // Re-allocate the backing store before the browser paints the fade-in.
    // The release above leaves the canvas at 0x0 and the render effect that
    // would re-size it is a passive effect, so it runs after the first
    // visible frame (PR #1698 review). This only restores the *size* — a
    // fresh backing store is transparent, so the map itself still fills in as
    // the preview and tiles arrive, exactly as on a first open.
    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        const canvas = canvasRef.current;
        if (canvas) {
            resizeCanvasForDpr(canvas, canvasSize[0], canvasSize[1], window.devicePixelRatio || 1);
        }
    }, [open, canvasSize]);

    const drawLargeMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            // Paint adapter over the tiled/PDF routes; created per draw from a
            // live (non-detached) source — see the render effect below.
            paint: DrawingPaint,
            canvasWidth: number,
            canvasHeight: number,
            placed: DrawingSpacePosition | null,
            currentScale: number,
            currentPanOffset: vec2,
        ) => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            const imgAspect = paint.width / paint.height;
            const canvasAspect = canvasWidth / canvasHeight;

            let scaledWidth: number;
            let scaledHeight: number;

            // Use "cover" scaling - image fills the canvas (may crop edges)
            if (imgAspect > canvasAspect) {
                scaledHeight = canvasHeight;
                scaledWidth = canvasHeight * imgAspect;
            } else {
                scaledWidth = canvasWidth;
                scaledHeight = canvasWidth / imgAspect;
            }

            // Apply zoom scale
            scaledWidth *= currentScale;
            scaledHeight *= currentScale;

            // Center the image on canvas, then apply pan offset
            const offsetX = (canvasWidth - scaledWidth) / 2 + currentPanOffset[0];
            const offsetY = (canvasHeight - scaledHeight) / 2 + currentPanOffset[1];

            // Store layout for position calculations
            imageLayoutRef.current = {
                x: offsetX,
                y: offsetY,
                width: scaledWidth,
                height: scaledHeight,
            };

            paint.paint(ctx, offsetX, offsetY, scaledWidth, scaledHeight);

            // Draw position indicator
            if (placed) {
                const posX = offsetX + placed.position[0] * scaledWidth;
                const posY = offsetY + placed.position[1] * scaledHeight;
                drawLargeMapPositionIndicator(ctx, posX, posY, placed.rotation);
            } else if (view && tempDrawing?.modelAB && tempDrawing?.drawingAB) {
                const camera = view.renderState.camera;
                const position = modelToDrawingSpace(tempDrawing.modelAB, tempDrawing.drawingAB, camera);
                const posX = offsetX + position.position[0] * scaledWidth;
                const posY = offsetY + position.position[1] * scaledHeight;
                drawLargeMapPositionIndicator(ctx, posX, posY, position.rotation);
            }
        },
        [view, tempDrawing],
    );

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !open) {
            return;
        }

        let indicatorPlacedDuringTouch = false;
        let currentPlacedPos: DrawingSpacePosition | null = null;
        let placementLayout: { x: number; y: number; width: number; height: number } | null = null;
        let isPanning = false;
        let lastPanPos: vec2 | null = null;
        let longPressTimer: ReturnType<typeof setTimeout> | null = null;
        let longPressStartPos: vec2 | null = null;

        const calculateRotation = (indicatorPos: vec2, fingerX: number, fingerY: number): number => {
            if (!placementLayout) {
                return 0;
            }

            const indicatorCanvasX = placementLayout.x + indicatorPos[0] * placementLayout.width;
            const indicatorCanvasY = placementLayout.y + indicatorPos[1] * placementLayout.height;

            const dx = fingerX - indicatorCanvasX;
            const dy = fingerY - indicatorCanvasY;

            const dist = Math.hypot(dx, dy);
            if (dist < MIN_ROTATION_DISTANCE) {
                return currentPlacedPos?.rotation ?? 0;
            }

            const angleRad = Math.atan2(dx, -dy);
            let angleDeg = angleRad * (180 / Math.PI);
            if (angleDeg < 0) {
                angleDeg += 360;
            }
            return angleDeg;
        };

        const cancelLongPress = () => {
            if (longPressTimer) {
                clearTimeout(longPressTimer);
                longPressTimer = null;
            }
            longPressStartPos = null;
        };

        const handlePointerDown = (e: PointerEvent) => {
            if (e.pointerType !== "touch" || !e.isPrimary) {
                return;
            }

            e.preventDefault();
            cancelLongPress();
            indicatorPlacedDuringTouch = false;
            currentPlacedPos = null;
            placementLayout = null;
            isPanning = false;
            lastPanPos = null;

            const rect = canvas.getBoundingClientRect();
            const fingerX = e.clientX - rect.left;
            const fingerY = e.clientY - rect.top;
            longPressStartPos = vec2.fromValues(fingerX, fingerY);

            longPressTimer = setTimeout(() => {
                longPressTimer = null;
                const layout = imageLayoutRef.current;

                if (layout && tempDrawing?.modelAB && tempDrawing?.drawingAB) {
                    if (
                        fingerX >= layout.x &&
                        fingerX <= layout.x + layout.width &&
                        fingerY >= layout.y &&
                        fingerY <= layout.y + layout.height
                    ) {
                        const percentX = (fingerX - layout.x) / layout.width;
                        const percentY = (fingerY - layout.y) / layout.height;

                        placementLayout = { ...layout };
                        const newPosition: DrawingSpacePosition = {
                            position: vec2.fromValues(percentX, percentY),
                            rotation: 0,
                        };
                        currentPlacedPos = newPosition;
                        indicatorPlacedDuringTouch = true;
                        setPlacedPosition(newPosition);
                    }
                }
            }, LONG_PRESS_DURATION);
        };

        const handlePointerMove = (e: PointerEvent) => {
            if (e.pointerType !== "touch" || !e.isPrimary) {
                return;
            }

            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const fingerX = e.clientX - rect.left;
            const fingerY = e.clientY - rect.top;

            // If indicator was placed, update rotation
            if (indicatorPlacedDuringTouch && currentPlacedPos && placementLayout) {
                const rotation = calculateRotation(currentPlacedPos.position, fingerX, fingerY);
                currentPlacedPos = { ...currentPlacedPos, rotation };
                setPlacedPosition(currentPlacedPos);
                return;
            }

            // Check if we should cancel long press and start panning
            if (longPressTimer) {
                const dist = vec2.dist(longPressStartPos!, vec2.fromValues(fingerX, fingerY));
                if (dist > LONG_PRESS_MOVE_THRESHOLD) {
                    cancelLongPress();
                    isPanning = true;
                    lastPanPos = vec2.fromValues(fingerX, fingerY);
                }
            }

            // Handle panning
            if (isPanning && lastPanPos) {
                const dx = fingerX - lastPanPos[0];
                const dy = fingerY - lastPanPos[1];
                setPanOffset((prev) => vec2.fromValues(prev[0] + dx, prev[1] + dy));
                lastPanPos = vec2.fromValues(fingerX, fingerY);
            }
        };

        const handlePointerUp = (e: PointerEvent) => {
            if (e.pointerType !== "touch" || !e.isPrimary) {
                return;
            }

            cancelLongPress();

            // If indicator was placed, confirm and navigate
            if (
                indicatorPlacedDuringTouch &&
                currentPlacedPos &&
                view &&
                tempLevel &&
                tempDrawing?.modelAB &&
                tempDrawing?.drawingAB
            ) {
                const elevation = tempLevel.elevation + EYE_ELEVATION_OFFSET;
                const modelSpacePos = drawingToModelSpace(
                    currentPlacedPos,
                    tempDrawing.modelAB,
                    tempDrawing.drawingAB,
                    elevation,
                );

                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Pinhole,
                        goTo: {
                            position: modelSpacePos.position,
                            rotation: modelSpacePos.rotation,
                            flyTime: SHEET_FLY_TIME,
                        },
                    }),
                );

                // Hold this sheet until the camera lands on it — the flight
                // streams camera updates from the old position and would
                // otherwise switch away from what the user just picked.
                dispatch(
                    navigateToDrawing({
                        drawingId: tempDrawing.id,
                        expiresAt: Date.now() + SHEET_FLY_TIME + NAVIGATION_PIN_GRACE,
                    }),
                );
                handleClose();
            }

            indicatorPlacedDuringTouch = false;
            currentPlacedPos = null;
            isPanning = false;
            lastPanPos = null;
        };

        const handlePointerCancel = () => {
            cancelLongPress();
            if (!indicatorPlacedDuringTouch) {
                setPlacedPosition(null);
            }
            indicatorPlacedDuringTouch = false;
            currentPlacedPos = null;
            isPanning = false;
            lastPanPos = null;
        };

        canvas.addEventListener("pointerdown", handlePointerDown);
        canvas.addEventListener("pointermove", handlePointerMove);
        canvas.addEventListener("pointerup", handlePointerUp);
        canvas.addEventListener("pointercancel", handlePointerCancel);

        return () => {
            cancelLongPress();
            canvas.removeEventListener("pointerdown", handlePointerDown);
            canvas.removeEventListener("pointermove", handlePointerMove);
            canvas.removeEventListener("pointerup", handlePointerUp);
            canvas.removeEventListener("pointercancel", handlePointerCancel);
        };
    }, [open, tempDrawing, tempLevel, view, handleClose, dispatch]);

    // Pinch zoom
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !open) {
            return;
        }

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const dist = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
                lastPinchDistRef.current = dist;

                // Store pinch center in screen coordinates (relative to canvas top-left)
                const rect = canvas.getBoundingClientRect();
                const pinchCenterX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
                const pinchCenterY = (touch1.clientY + touch2.clientY) / 2 - rect.top;
                pinchScreenPosRef.current = vec2.fromValues(pinchCenterX, pinchCenterY);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 2 || !pinchScreenPosRef.current || lastPinchDistRef.current === null) {
                return;
            }

            e.preventDefault();
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];

            const dist = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
            const pinchDelta = dist / lastPinchDistRef.current;
            const pinchScreenPos = pinchScreenPosRef.current;

            const newScale = Math.max(0.5, Math.min(5, scale * pinchDelta));

            const [canvasWidth, canvasHeight] = canvasSize;
            const imageCenterX = canvasWidth / 2 + panOffset[0];
            const imageCenterY = canvasHeight / 2 + panOffset[1];

            const pinchOffsetX = pinchScreenPos[0] - imageCenterX;
            const pinchOffsetY = pinchScreenPos[1] - imageCenterY;

            const scaleRatio = newScale / scale;
            const newPanOffsetX = panOffset[0] - pinchOffsetX * (scaleRatio - 1);
            const newPanOffsetY = panOffset[1] - pinchOffsetY * (scaleRatio - 1);

            setScale(newScale);
            setPanOffset(vec2.fromValues(newPanOffsetX, newPanOffsetY));

            lastPinchDistRef.current = dist;
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (e.touches.length < 2) {
                lastPinchDistRef.current = null;
                pinchScreenPosRef.current = null;
            }
        };

        canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
        canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
        canvas.addEventListener("touchend", handleTouchEnd);
        canvas.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            canvas.removeEventListener("touchstart", handleTouchStart);
            canvas.removeEventListener("touchmove", handleTouchMove);
            canvas.removeEventListener("touchend", handleTouchEnd);
            canvas.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [open, scale, panOffset, canvasSize]);

    // Canvas rendering
    useEffect(() => {
        if (!open) {
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const [canvasWidth, canvasHeight] = canvasSize;
        const dpr = window.devicePixelRatio || 1;

        resizeCanvasForDpr(canvas, canvasWidth, canvasHeight, dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (!tempDrawing) {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            return;
        }

        // The large map shows the full-resolution image only, so it's driven by
        // the tiles state (not the preview): the loading indicator stays up until
        // the tiles finish rasterizing rather than flashing a blurry preview.
        const { status } = tempDrawing.drawingData.asyncState;
        switch (status) {
            case AsyncStatus.Initial:
                downloadDrawingData({
                    dispatch,
                    drawingId: tempDrawing.id,
                    url: tempDrawing.drawingData.url,
                    telemetrySource: "miniMap.largeMapMobile.fetchDrawingData",
                    tiling: buildTilingRequest(
                        tilingEnabled,
                        procoreContext,
                        sceneId,
                        tempDrawing.drawingData,
                        isMobileDesign,
                    ),
                });
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Loading:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success: {
                // Tiled route paints visible tiles; PDF route paints the
                // decoded bitmap (undefined while decoding / after release).
                const paint = tiledMap.active
                    ? makeTiledPaint(tiledMap, canvasWidth, canvasHeight, dpr)
                    : makeBitmapPaint(img);
                if (paint) {
                    drawLargeMap(ctx, paint, canvasWidth, canvasHeight, placedPosition, scale, panOffset);
                } else {
                    // Not ready yet; this effect re-runs once `useDrawingImage`
                    // resolves a bitmap / tiles arrive (img/tiledMap are in the deps).
                    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                }
                break;
            }
            case AsyncStatus.Error:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            default:
        }
    }, [
        open,
        tempDrawing,
        canvasSize,
        drawLargeMap,
        placedPosition,
        scale,
        panOffset,
        dispatch,
        img,
        tiledMap,
        tilingEnabled,
        isMobileDesign,
        procoreContext,
        sceneId,
    ]);

    const portalContainer = document.getElementById("mobile-hud-container");
    if (!portalContainer) {
        return null;
    }

    return createPortal(
        <LargeMapContainer open={open} isFullscreenMode={isFullscreenMode}>
            <CanvasContainer ref={containerRef}>
                <canvas
                    ref={canvasRef}
                    style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        touchAction: "none",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        userSelect: "none",
                    }}
                />
                {tempDrawing &&
                    (tempDrawing.drawingData.asyncState.status === AsyncStatus.Loading ||
                        tempDrawing.drawingData.asyncState.status === AsyncStatus.Initial) && (
                        <LoadingOverlay>
                            <Spinner size="lg" loading={true} />
                            <Typography>{t("loadingDrawing")}</Typography>
                        </LoadingOverlay>
                    )}
                {isFullscreenMode && (
                    <CloseButtonContainer>
                        <Button
                            variant="secondary"
                            icon={<Clear color="white" />}
                            aria-label="Close"
                            onClick={handleClose}
                            style={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                padding: 0,
                                backgroundColor: "#171A1C",
                                borderRadius: 4,
                            }}
                        />
                    </CloseButtonContainer>
                )}
                {canNavigate && (
                    <NavArrowsContainer>
                        <Button
                            variant="secondary"
                            icon={<ArrowLeft color="white" />}
                            aria-label="Previous sheet"
                            onClick={handlePrevDrawing}
                            style={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                padding: 0,
                                backgroundColor: "#171A1C",
                                borderRadius: 4,
                            }}
                        />
                        <Button
                            variant="secondary"
                            icon={<ArrowRight color="white" />}
                            aria-label="Next sheet"
                            onClick={handleNextDrawing}
                            style={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                padding: 0,
                                backgroundColor: "#171A1C",
                                borderRadius: 4,
                            }}
                        />
                    </NavArrowsContainer>
                )}
                {showHelperText && (
                    <HelperTextContainer
                        centered={isFullscreenMode || canvasSize[0] < HELPER_TEXT_CENTER_BREAKPOINT}
                        topOffset={topOffset}
                    >
                        <Typography intent="body" style={{ fontSize: 14, color: "#232729" }}>
                            {t("largeMapHelperText")}
                        </Typography>
                        <Button
                            variant="secondary"
                            icon={<Clear />}
                            aria-label="Dismiss"
                            onClick={handleDismissHelperText}
                            style={{ width: 40, height: 40, minWidth: 40, padding: 0, flexShrink: 0 }}
                        />
                    </HelperTextContainer>
                )}
                <LevelsDrawerMobile
                    open={levelsDrawerOpen}
                    largeMapOpen={open}
                    onInsideLevelChange={setIsInsideLevel}
                    fullWidth={isNarrowLayout}
                    topOffset={topOffset}
                />
            </CanvasContainer>
        </LargeMapContainer>,
        portalContainer,
    );
}
